import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import { createTask, fetchTasks, fetchJob } from "../../api/companyApi";
import { fetchFunctionsByRoles } from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function TaskCreatePage() {
  const { businessDomainId, jobId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const lang = navigator.language;

  const [form, setForm] = useState({
    taskName: "",
    taskDescription: "",
    url: "",
  });
  const [selectedFunctionId, setSelectedFunctionId] = useState("");
  const [functions, setFunctions] = useState([]);
  const [job, setJob] = useState(null);
  const [existingTasks, setExistingTasks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [tasks, jobData] = await Promise.all([
          fetchTasks(jobId),
          fetchJob(jobId),
        ]);
        if (Array.isArray(tasks)) {
          setExistingTasks(tasks);
        }
        setJob(jobData);
        if (jobData?.roles && Array.isArray(jobData.roles) && jobData.roles.length > 0) {
          const funcs = await fetchFunctionsByRoles(jobData.roles);
          setFunctions(Array.isArray(funcs) ? funcs : []);
        }
      } catch {
        // ignore
      }
    })();
  }, [jobId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFunctionId) {
      notify.error(t("task.function.required"));
      return;
    }
    const selectedFunction = functions.find((f) => String(f.id) === selectedFunctionId);
    const trimmed = form.taskName.trim();
    const isDuplicate = existingTasks.some(
      (t) => t.taskName?.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (isDuplicate) {
      notify.error(t("task.error.duplicate"));
      return;
    }
    try {
      await createTask({
        companyId: COMPANY_ID,
        jobId,
        jobName: job?.jobName || "",
        taskName: form.taskName,
        taskDescription: form.taskDescription,
        url: form.url,
        functionId: selectedFunction.id,
        functionName: lang === "ja" ? selectedFunction.japaneseName : selectedFunction.englishName,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/jobs/${jobId}`, { state: { tab: 1 } });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("task.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              label={t("task.name")}
              name="taskName"
              value={form.taskName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("task.description")}
              name="taskDescription"
              value={form.taskDescription}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t("common.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("task.function")}</InputLabel>
              <Select
                value={selectedFunctionId}
                onChange={(e) => {
                  const funcId = e.target.value;
                  setSelectedFunctionId(funcId);
                  const selectedFunc = functions.find((f) => String(f.id) === funcId);
                  if (selectedFunc) {
                    const funcName = lang === "ja" ? selectedFunc.japaneseName : selectedFunc.englishName;
                    setForm((prev) => ({
                      ...prev,
                      taskName: funcName || prev.taskName,
                      taskDescription: funcName || prev.taskDescription,
                    }));
                  }
                }}
                label={t("task.function")}
              >
                {functions.map((func) => (
                  <MenuItem key={func.id} value={String(func.id)}>
                    {lang === "ja" ? func.japaneseName : func.englishName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.add")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
